
import React, { Component, ErrorInfo, ReactNode, Suspense } from 'react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Wifi, ShieldAlert, Database, XCircle } from "lucide-react";
import { toast } from "sonner";
import { handleError, ErrorSeverity, ErrorCategory } from "@/utils/errorHandler";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorCategory: ErrorCategory;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorCategory: ErrorCategory.UNEXPECTED
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Filter out suspense-related errors
    if (error.message.includes('suspended') || error.message.includes('Suspense')) {
      return {
        hasError: false,
        error: null,
        errorCategory: ErrorCategory.UNEXPECTED // Changed from UI to UNEXPECTED
      };
    }
    
    // Determine error category
    let errorCategory = ErrorCategory.UNEXPECTED;
    const errorMsg = error.message.toLowerCase();
    
    if (errorMsg.includes('network') || errorMsg.includes('fetch') || errorMsg.includes('timeout')) {
      errorCategory = ErrorCategory.NETWORK;
    } else if (errorMsg.includes('auth') || errorMsg.includes('login') || errorMsg.includes('sign in')) {
      errorCategory = ErrorCategory.AUTHENTICATION;
    } else if (errorMsg.includes('permission') || errorMsg.includes('access') || errorMsg.includes('forbidden')) {
      errorCategory = ErrorCategory.AUTHORIZATION;
    } else if (errorMsg.includes('database') || errorMsg.includes('query') || errorMsg.includes('sql')) {
      errorCategory = ErrorCategory.DATABASE;
    }
    
    return {
      hasError: true,
      error,
      errorCategory
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Skip logging for suspense-related errors
    if (error.message.includes('suspended') || error.message.includes('Suspense')) {
      console.log('Suspense error caught and ignored:', error.message);
      return;
    }
    
    // Use our error handler
    handleError(
      error,
      `Error in ${this.props.componentName || 'component'}`,
      ErrorSeverity.ERROR,
      {
        operation: 'reactRendering',
        source: this.props.componentName || 'unknown',
        category: this.state.errorCategory,
        additionalInfo: { errorInfo }
      },
      false // Don't show toast here since we have custom UI
    );
    
    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    toast.info('Attempting to recover component');
    this.setState({
      hasError: false,
      error: null,
      errorCategory: ErrorCategory.UNEXPECTED
    });
  };

  getErrorIcon = (): ReactNode => {
    switch (this.state.errorCategory) {
      case ErrorCategory.NETWORK:
        return <Wifi className="h-4 w-4" />;
      case ErrorCategory.AUTHENTICATION:
      case ErrorCategory.AUTHORIZATION:
        return <ShieldAlert className="h-4 w-4" />;
      case ErrorCategory.DATABASE:
        return <Database className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  getErrorTitle = (): string => {
    switch (this.state.errorCategory) {
      case ErrorCategory.NETWORK:
        return "Network Error";
      case ErrorCategory.AUTHENTICATION:
        return "Authentication Error";
      case ErrorCategory.AUTHORIZATION:
        return "Access Denied";
      case ErrorCategory.DATABASE:
        return "Data Error";
      case ErrorCategory.VALIDATION:
        return "Validation Error";
      default:
        return "Something went wrong";
    }
  };

  getSuggestedAction = (): string => {
    switch (this.state.errorCategory) {
      case ErrorCategory.NETWORK:
        return "Check your internet connection and try again.";
      case ErrorCategory.AUTHENTICATION:
        return "You may need to sign in again.";
      case ErrorCategory.AUTHORIZATION:
        return "You don't have permission to access this resource.";
      case ErrorCategory.DATABASE:
        return "There was an issue retrieving data from the server.";
      default:
        return "An unexpected error occurred.";
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Show fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="p-4">
          <Alert variant="destructive">
            {this.getErrorIcon()}
            <AlertTitle>{this.getErrorTitle()}</AlertTitle>
            <AlertDescription>
              <p className="mb-2">
                {this.state.error?.message || this.getSuggestedAction()}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={this.handleReset}
                className="flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" />
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
